import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon, { metricIcon } from '../design/Icon';
import { Sheet, Stepper, fmtDateShort, todayStr, addDaysStr, dayLetter } from '../design/atoms';

/* SPEC 09 Daily log — week strip, pinned steppers, metric catalog, RPE, save. */

interface OutletCtx {
  activeGymId: string | null;
  hasClassScheduling: boolean;
  loading: boolean;
}

interface PinnedMetric {
  id: string;
  metricKey: string;
  name: string;
  category: string | null;
  unit: string | null;
  direction: string;
  step: number;
  position: number;
  value: number | null;
  previousValue: number | null;
}

interface LogData {
  date: string;
  maxDate: string;
  pinned: PinnedMetric[];
  rpe: string | null;
  pinnedDone: number;
  pinnedTotal: number;
  week: { date: string; logged: boolean }[];
  streak: { current: number; best: number };
}

interface CatalogMetric {
  id: string;
  metricKey: string;
  displayName: string;
  metricCategory: string | null;
  metricUnit: string | null;
}

const RPE_OPTIONS = [
  { value: 'easy', label: 'Easy' },
  { value: 'solid', label: 'Solid' },
  { value: 'hard', label: 'Hard' },
  { value: 'wrecked', label: 'Wrecked' },
] as const;

const MAX_PINNED = 6;

export default function MemberDailyLogPage() {
  const { token } = useMemberAuth();
  const { activeGymId, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const today = todayStr();
  const date = searchParams.get('date') ?? today;

  const [data, setData] = useState<LogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, number>>({});
  const [rpe, setRpe] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const load = useCallback((keepEdits = false) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    mfetch<{ data: LogData }>(token, '/member/log', { params: { date } })
      .then(r => {
        setData(r.data);
        if (keepEdits) {
          const ids = new Set(r.data.pinned.map(p => p.id));
          setEdits(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => ids.has(id))));
        } else {
          setEdits({});
          setRpe(r.data.rpe);
        }
      })
      .catch(e => setError(e instanceof MemberApiError ? e.message : "Could not load your log"))
      .finally(() => setLoading(false));
  }, [token, date]);

  useEffect(() => { load(false); }, [load]);

  const maxDate = data?.maxDate && data.maxDate < today ? data.maxDate : today;
  const canForward = date < maxDate;
  const futureBlocked = date > maxDate;

  const stepDate = (days: number) => {
    if (saving || saved) return;
    const next = addDaysStr(date, days);
    if (next > maxDate) return;
    setSearchParams({ date: next }, { replace: true });
  };

  const save = async () => {
    if (!token || !data || saving || saved) return;
    setSaving(true);
    setSaveError(null);
    try {
      const entries = Object.entries(edits).map(([metricDefinitionId, value]) => ({ metricDefinitionId, value }));
      await mfetch(token, '/member/log', { method: 'PUT', body: { date, entries, rpe } });
      setSaving(false);
      setSaved(true);
      setTimeout(() => navigate('/app/progress'), 900);
    } catch (e) {
      setSaving(false);
      setSaveError(e instanceof MemberApiError ? e.message : 'Could not save your log. Please try again.');
    }
  };

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Daily log</div>
        <div className="card flat"><div className="sub wrap">Join a gym to start logging your progress.</div></div>
      </div>
    );
  }

  return (
    <div className="mpage">
      {/* Header */}
      <div className="between" style={{ paddingTop: 8 }}>
        <button className="icon-btn" aria-label="Close" onClick={() => navigate('/app/progress')}>
          <Icon n="x" s={18} c="var(--ink-2)" />
        </button>
        <div className="row" style={{ gap: 6 }}>
          <button className="icon-btn" aria-label="Previous day" onClick={() => stepDate(-1)}>
            <Icon n="left" s={16} c="var(--ink-2)" />
          </button>
          <div className="col" style={{ alignItems: 'center', gap: 2, minWidth: 96 }}>
            <div className="lbl">Daily log</div>
            <div className="sub num">{fmtDateShort(date)}</div>
          </div>
          <button
            className="icon-btn"
            aria-label="Next day"
            disabled={!canForward}
            style={{ opacity: canForward ? 1 : 0.35 }}
            onClick={() => stepDate(1)}
          >
            <Icon n="right" s={16} c="var(--ink-2)" />
          </button>
        </div>
        <div style={{ width: 38, flex: '0 0 38px' }} />
      </div>

      {loading ? (
        <>
          <div className="skel" style={{ height: 58 }} />
          <div className="skel" style={{ height: 240 }} />
          <div className="skel" style={{ height: 52 }} />
        </>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => load(false)}>Retry</button>
        </div>
      ) : data ? (
        <>
          {/* Week strip (Mon–Sun of the viewed week) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {data.week.map(d => (
              <div key={d.date} className="col" style={{ alignItems: 'center', gap: 5 }}>
                <span className="tiny">{dayLetter(d.date)}</span>
                <span className={`dot${d.date === date ? ' on' : d.logged ? ' soft' : ''}`} style={{ width: 24 }}>
                  {d.logged ? '•' : ''}
                </span>
              </div>
            ))}
          </div>

          <div className="between" style={{ padding: '2px 2px 0' }}>
            <div className="lbl">Pinned today</div>
            <div className="tiny">Tap to adjust · saves when you tap Save</div>
          </div>

          {/* Pinned metric rows */}
          {data.pinned.length > 0 && (
            <div className="card" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {data.pinned.map(m => {
                const touched = m.id in edits;
                const logged = touched || m.value != null;
                const current = edits[m.id] ?? m.value ?? m.previousValue ?? 0;
                return (
                  <div key={m.id} className="lrow">
                    <div className={`li${logged ? ' gold' : ''}`}>
                      <Icon n={metricIcon(m.category ?? '', m.name)} s={17} c={logged ? 'var(--gold-2)' : 'var(--ink-3)'} />
                    </div>
                    <div className="col" style={{ gap: 1 }}>
                      <span className="mid trunc">{m.name}</span>
                      <span className="tiny trunc">{[m.category, m.unit].filter(Boolean).join(' · ')}</span>
                    </div>
                    <Stepper
                      value={current}
                      step={m.step}
                      onChange={v => setEdits(prev => ({ ...prev, [m.id]: v }))}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Add from catalog */}
          <button className="card dashed" style={{ textAlign: 'center', minHeight: 44 }} onClick={() => setCatalogOpen(true)}>
            <span className="sub" style={{ fontWeight: 600 }}>＋ Add from the metric catalog</span>
          </button>

          {/* RPE */}
          <div className="lbl" style={{ padding: '2px 2px 0' }}>How did today feel?</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {RPE_OPTIONS.map(o => (
              <button
                key={o.value}
                className={`chip${rpe === o.value ? ' on' : ''}`}
                onClick={() => setRpe(prev => (prev === o.value ? null : o.value))}
              >
                {o.label}
              </button>
            ))}
          </div>

          {saveError && (
            <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
              <span className="sub wrap" style={{ color: 'var(--clay)' }}>{saveError}</span>
            </div>
          )}

          <button className="btn gold block" disabled={saving || saved || futureBlocked} onClick={save}>
            {saved ? 'Saved' : saving ? 'Saving…' : "Save today's log"}
          </button>
        </>
      ) : null}

      {catalogOpen && token && data && (
        <CatalogSheet
          token={token}
          date={date}
          initial={data.pinned.map(p => p.id)}
          onClose={() => setCatalogOpen(false)}
          onSaved={() => {
            setCatalogOpen(false);
            load(true);
          }}
        />
      )}
    </div>
  );
}

// ── Metric catalog sheet (search + category filter + pin up to 6) ───────────
function CatalogSheet({
  token, date, initial, onClose, onSaved,
}: {
  token: string;
  date: string;
  initial: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [all, setAll] = useState<CatalogMetric[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState<string[]>(initial);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoadError(null);
    mfetch<{ data: CatalogMetric[] }>(token, '/member/progress/metrics')
      .then(r => setAll(r.data))
      .catch(e => setLoadError(e instanceof MemberApiError ? e.message : 'Could not load the metric catalog'));
  }, [token, reload]);

  const categories = useMemo(
    () => Array.from(new Set((all ?? []).map(m => m.metricCategory).filter((c): c is string => !!c))),
    [all]
  );

  const q = query.trim().toLowerCase();
  const visible = (all ?? []).filter(m =>
    (category === 'all' || m.metricCategory === category) &&
    (!q || m.displayName.toLowerCase().includes(q) || m.metricKey.toLowerCase().includes(q))
  );

  const toggle = (id: string) =>
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_PINNED) return prev;
      return [...prev, id];
    });

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await mfetch(token, '/member/pinned-metrics', {
        method: 'PUT',
        body: { metricDefinitionIds: selected, date },
      });
      onSaved();
    } catch (e) {
      setSaving(false);
      setSaveError(e instanceof MemberApiError ? e.message : 'Could not save your pinned metrics.');
    }
  };

  return (
    <Sheet onClose={saving ? () => {} : onClose}>
      <div className="between">
        <div className="col" style={{ gap: 2 }}>
          <div className="dp dp-m">Metric catalog</div>
          <div className="tiny">{selected.length} of {MAX_PINNED} pinned</div>
        </div>
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      <input
        className="minput"
        placeholder="Search metrics"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />

      {categories.length > 0 && (
        <div className="hscroll">
          <button className={`chip${category === 'all' ? ' on' : ''}`} onClick={() => setCategory('all')}>All</button>
          {categories.map(c => (
            <button key={c} className={`chip${category === c ? ' on' : ''}`} onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>
      )}

      {all === null && !loadError ? (
        <div className="col" style={{ gap: 9 }}>
          {[48, 48, 48].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
        </div>
      ) : loadError ? (
        <div className="card flat">
          <div className="sub wrap">{loadError}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => { setAll(null); setReload(n => n + 1); }}>
            Retry
          </button>
        </div>
      ) : (
        <div className="card flat" style={{ padding: '2px 14px', maxHeight: '42dvh', overflowY: 'auto' }}>
          {visible.map(m => {
            const on = selected.includes(m.id);
            return (
              <button key={m.id} className="lrow" onClick={() => toggle(m.id)}>
                <div className={`li${on ? ' gold' : ''}`}>
                  {on
                    ? <Icon n="check" s={16} c="var(--gold-2)" />
                    : <Icon n={metricIcon(m.metricCategory ?? '', m.displayName)} s={16} c="var(--ink-3)" />}
                </div>
                <div className="col" style={{ gap: 1 }}>
                  <span className="mid trunc">{m.displayName}</span>
                  <span className="tiny trunc">{[m.metricCategory, m.metricUnit].filter(Boolean).join(' · ')}</span>
                </div>
              </button>
            );
          })}
          {visible.length === 0 && (
            <div className="sub wrap" style={{ padding: '14px 0' }}>No metrics match your search.</div>
          )}
        </div>
      )}

      {saveError && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>{saveError}</span>
        </div>
      )}

      <button className="btn gold block" disabled={saving || all === null} onClick={save}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </Sheet>
  );
}
